<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="toolbar" id="kt_toolbar">
      <div id="kt_toolbar_container" class="container-fluid d-flex flex-stack">
        <div class="page-title d-flex align-items-center flex-wrap me-3 mb-5 mb-lg-0">
          <h1 class="d-flex align-items-center text-dark fw-bolder fs-3 my-1">
            {{ $t('landlord.plans.title') }}
          </h1>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button @click="openCreateModal()" class="btn btn-sm btn-primary">
            <i class="fas fa-plus me-1"></i> {{ $t('landlord.plans.create') }}
          </button>
        </div>
      </div>
    </div>
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-fluid">

        <div v-if="loading" class="text-center py-10">
          <span class="spinner-border text-primary"></span>
        </div>
        <div v-else-if="!plans.length" class="text-center py-10 text-gray-500">
          {{ $t('landlord.plans.no_results') }}
        </div>

        <!-- Plans cards -->
        <div v-else class="row g-5 g-xl-8">
          <div v-for="plan in plans" :key="plan.id" class="col-xl-4 col-md-6">
            <div class="card card-flush h-100">
              <div class="card-header border-0 pt-6 pb-0">
                <div class="card-title">
                  <h3 class="fw-bolder">{{ plan.name }}</h3>
                </div>
                <div class="card-toolbar">
                  <span class="badge" :class="plan.is_active ? 'badge-light-success' : 'badge-light-danger'">
                    {{ plan.is_active ? $t('landlord.plans.active') : $t('landlord.plans.inactive') }}
                  </span>
                </div>
              </div>
              <div class="card-body py-5">
                <div class="mb-4">
                  <span class="fs-2x fw-bolder text-primary">{{ formatPrice(plan.price) }}</span>
                  <span v-if="plan.billing_period" class="text-muted fs-7"> / {{ $t('landlord.plans.period_' + plan.billing_period) }}</span>
                </div>
                <p v-if="plan.description" class="text-gray-500 fs-7 mb-4">{{ plan.description }}</p>

                <!-- Modules list -->
                <div class="mb-4">
                  <div class="fw-bold text-gray-800 fs-7 mb-2">{{ $t('landlord.plans.modules_included') }} ({{ plan.modules ? plan.modules.length : 0 }})</div>
                  <div v-if="plan.modules && plan.modules.length">
                    <div v-for="mod in plan.modules" :key="mod.id" class="d-flex align-items-center mb-1">
                      <i class="fas fa-check-circle text-success me-2 fs-8"></i>
                      <span class="fs-7">{{ mod.name }}</span>
                    </div>
                  </div>
                  <div v-else class="text-gray-400 fs-8">{{ $t('landlord.plans.no_modules') }}</div>
                </div>
              </div>
              <div class="card-footer border-0 pt-0 d-flex gap-2">
                <router-link :to="`/landlord/plans/${plan.id}`" class="btn btn-sm btn-light-primary flex-grow-1">
                  <i class="fas fa-cog me-1"></i> {{ $t('landlord.plans.manage') }}
                </router-link>
                <button @click="openEditModal(plan)" class="btn btn-sm btn-light-info">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="onDelete(plan)" class="btn btn-sm btn-light-danger">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div class="modal fade" id="planModal" data-bs-backdrop="static" tabindex="-1">
      <!-- largeur moyenne, deux colonnes : textes à gauche, prix et réglages à droite -->
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <form @submit.prevent="onSubmit" class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? $t('landlord.plans.edit_title') : $t('landlord.plans.create_title') }}</h5>
              <button type="button" class="btn-close" @click="closeModal"></button>
            </div>
            <div class="modal-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-4">
                    <label class="form-label fw-bold required">{{ $t('landlord.plans.field_name') }}</label>
                    <input v-model="form.name" type="text" class="form-control form-control-solid" />
                  </div>
                  <div class="mb-4">
                    <label class="form-label fw-bold">{{ $t('landlord.plans.field_tagline') }}</label>
                    <input v-model="form.tagline" type="text" maxlength="150" class="form-control form-control-solid"
                           :placeholder="$t('landlord.plans.field_tagline_placeholder')" />
                  </div>
                  <div class="mb-4">
                    <label class="form-label fw-bold">{{ $t('landlord.plans.field_description') }}</label>
                    <textarea v-model="form.description" class="form-control form-control-solid" rows="2"></textarea>
                  </div>
                  <div class="mb-4">
                    <label class="form-label fw-bold">{{ $t('landlord.plans.field_ideal_for') }}</label>
                    <input v-model="form.ideal_for" type="text" maxlength="150" class="form-control form-control-solid"
                           :placeholder="$t('landlord.plans.field_ideal_for_placeholder')" />
                  </div>
                  <div class="mb-4">
                    <label class="form-label fw-bold">{{ $t('landlord.plans.field_features') }}</label>
                    <textarea v-model="form.features_text" class="form-control form-control-solid" rows="5"
                              :placeholder="$t('landlord.plans.field_features_placeholder')"></textarea>
                    <div class="form-text">{{ $t('landlord.plans.field_features_help') }}</div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="row">
                    <div class="col-6 mb-4">
                      <label class="form-label fw-bold">{{ $t('landlord.plans.field_price') }}</label>
                      <input v-model.number="form.price" type="number" step="0.01" min="0" class="form-control form-control-solid" />
                    </div>
                    <div class="col-6 mb-4">
                      <label class="form-label fw-bold">{{ $t('landlord.plans.field_price_yearly') }}</label>
                      <input v-model.number="form.price_yearly" type="number" step="0.01" min="0" class="form-control form-control-solid"
                             :placeholder="$t('landlord.plans.field_price_yearly_placeholder')" />
                    </div>
                    <div class="col-6 mb-4">
                      <label class="form-label fw-bold">{{ $t('landlord.plans.field_max_users') }}</label>
                      <input v-model.number="form.max_users" type="number" step="1" min="0" class="form-control form-control-solid" />
                      <div class="form-text">{{ $t('landlord.plans.field_max_users_help') }}</div>
                    </div>
                    <div class="col-6 mb-4">
                      <label class="form-label fw-bold">{{ $t('landlord.plans.field_billing_period') }}</label>
                      <select v-model="form.billing_period" class="form-select form-select-solid">
                        <option :value="null">--</option>
                        <option value="monthly">{{ $t('landlord.plans.period_monthly') }}</option>
                        <option value="yearly">{{ $t('landlord.plans.period_yearly') }}</option>
                      </select>
                    </div>
                  </div>
                  <!-- ordre des offres sur le site et dans « Mon abonnement » (vide = après les autres, par prix) -->
                  <div class="mb-4">
                    <label class="form-label fw-bold">{{ $t('landlord.plans.field_position') }}</label>
                    <input v-model.number="form.position" type="number" min="1" max="999" class="form-control form-control-solid"
                           :placeholder="$t('landlord.plans.field_position_placeholder')" />
                  </div>
                  <div class="form-check form-switch">
                    <input v-model="form.is_active" class="form-check-input" type="checkbox" id="planActiveSwitch" />
                    <label class="form-check-label" for="planActiveSwitch">{{ $t('landlord.plans.active') }}</label>
                  </div>
                  <div class="form-check form-switch mt-3">
                    <input v-model="form.is_featured" class="form-check-input" type="checkbox" id="planFeaturedSwitch" />
                    <label class="form-check-label" for="planFeaturedSwitch">{{ $t('landlord.plans.featured') }}</label>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" @click="closeModal" class="btn btn-light">{{ $t('landlord.plans.cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="submitting">
                <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ isEditing ? $t('landlord.plans.save') : $t('landlord.plans.create') }}
              </button>
            </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    name: 'LandlordPlansIndex',
    data() {
        return {
            loading: false,
            submitting: false,
            isEditing: false,
            editingId: null,
            form: this.emptyForm(),
            modalInstance: null,
        }
    },
    computed: {
        ...mapGetters('landlord', ['plans']),
    },
    mounted() {
        this.loadPlans()
    },
    methods: {
        ...mapActions('landlord', ['fetchPlans', 'createPlan', 'updatePlan', 'deletePlan']),

        emptyForm() {
            return { name: '', tagline: '', description: '', ideal_for: '', is_featured: false, position: null, price: 0, price_yearly: null, max_users: 0, features_text: '', billing_period: null, is_active: true }
        },

        formatPrice(price) {
            return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA'
        },

        loadPlans() {
            this.loading = true
            this.fetchPlans().finally(() => { this.loading = false })
        },

        openCreateModal() {
            this.isEditing = false
            this.editingId = null
            this.form = this.emptyForm()
            this.showModal()
        },

        openEditModal(plan) {
            this.isEditing = true
            this.editingId = plan.id
            this.form = {
                name: plan.name,
                tagline: plan.tagline || '',
                description: plan.description || '',
                ideal_for: plan.ideal_for || '',
                is_featured: !!plan.is_featured,
                position: plan.position ?? null,
                price: plan.price,
                price_yearly: plan.price_yearly,
                max_users: plan.max_users ?? 0,
                // une ligne par point affiché sur la carte de l'offre (landing)
                features_text: (plan.features || []).join('\n'),
                billing_period: plan.billing_period,
                is_active: plan.is_active,
            }
            this.showModal()
        },

        showModal() {
            if (!this.modalInstance) {
                this.modalInstance = new bootstrap.Modal(document.getElementById('planModal'))
            }
            this.modalInstance.show()
        },

        closeModal() {
            if (this.modalInstance) {
                this.modalInstance.hide()
            }
        },

        onSubmit() {
            this.submitting = true
            const { features_text, ...fields } = this.form
            const data = {
                ...fields,
                // champ vidé = prix annuel calculé par la landing (mensuel × 10)
                price_yearly: fields.price_yearly === '' ? null : fields.price_yearly,
                max_users: fields.max_users || 0,
                position: fields.position || null,
                features: features_text.split('\n').map((line) => line.trim()).filter(Boolean),
            }
            const action = this.isEditing
                ? this.updatePlan({ planId: this.editingId, data })
                : this.createPlan(data)

            action.then(() => {
                window.toastr['success'](
                    this.isEditing
                        ? i18n.global.t('landlord.plans.updated')
                        : i18n.global.t('landlord.plans.created')
                )
                this.closeModal()
                this.loadPlans()
            }).finally(() => {
                this.submitting = false
            })
        },

        onDelete(plan) {
            Swal.fire({
                text: this.$t('landlord.plans.confirm_delete', { name: plan.name }),
                icon: 'warning',
                showCancelButton: true,
                buttonsStyling: false,
                confirmButtonText: i18n.global.t('swal.yes'),
                cancelButtonText: i18n.global.t('swal.no'),
                customClass: {
                    confirmButton: 'btn fw-bold btn-danger',
                    cancelButton: 'btn fw-bold btn-secondary'
                }
            }).then((result) => {
                if (result.value) {
                    this.deletePlan(plan.id).then(() => {
                        window.toastr['success'](i18n.global.t('landlord.plans.deleted'))
                        this.loadPlans()
                    })
                }
            })
        },
    }
}
</script>
